import type { RetailProduct } from '@pms/shared';

/** A line in the local terminal cart — a snapshot of the product at add time. */
export interface CartLine {
  productId: string;
  name: string;
  sku: string;
  /** Unit price as a number (snapshot from the grid product). */
  unitPrice: number;
  /** Drives the aggregated tax estimate. */
  taxable: boolean;
  qty: number;
  /** On-hand at add/refetch time — soft-warn only; tender is the hard check. */
  stockOnHand: number;
}

export interface CartState {
  lines: CartLine[];
}

export type CartAction =
  | { type: 'ADD'; product: RetailProduct }
  | { type: 'INC'; productId: string }
  | { type: 'DEC'; productId: string }
  | { type: 'SET_QTY'; productId: string; qty: number }
  | { type: 'REMOVE'; productId: string }
  | { type: 'CLEAR' };

export const emptyCart: CartState = { lines: [] };

export function cartReducer(state: CartState, action: CartState | CartAction): CartState {
  // Allow direct re-init (used nowhere but keeps the reducer total).
  if (!('type' in action)) return action;

  switch (action.type) {
    case 'ADD': {
      const { product } = action;
      const existing = state.lines.find((l) => l.productId === product.id);
      if (existing) {
        return {
          lines: state.lines.map((l) =>
            l.productId === product.id ? { ...l, qty: l.qty + 1 } : l,
          ),
        };
      }
      return {
        lines: [
          ...state.lines,
          {
            productId: product.id,
            name: product.name,
            sku: product.sku,
            unitPrice: parseFloat(product.price),
            taxable: product.taxable,
            qty: 1,
            stockOnHand: product.stockOnHand,
          },
        ],
      };
    }
    case 'INC':
      return {
        lines: state.lines.map((l) =>
          l.productId === action.productId ? { ...l, qty: l.qty + 1 } : l,
        ),
      };
    case 'DEC':
      // At qty 1, decrementing removes the line (Bills food-line immediacy).
      return {
        lines: state.lines.flatMap((l) => {
          if (l.productId !== action.productId) return [l];
          if (l.qty <= 1) return [];
          return [{ ...l, qty: l.qty - 1 }];
        }),
      };
    case 'SET_QTY': {
      const qty = Math.max(1, Math.floor(action.qty || 1));
      return {
        lines: state.lines.map((l) =>
          l.productId === action.productId ? { ...l, qty } : l,
        ),
      };
    }
    case 'REMOVE':
      return { lines: state.lines.filter((l) => l.productId !== action.productId) };
    case 'CLEAR':
      return emptyCart;
    default:
      return state;
  }
}

/** Round to 2dp without binary-float drift. */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export interface CartTotals {
  subtotal: number;
  tax: number;
  total: number;
}

/** Optimistic totals. Tax = Σ(taxable line totals) * rate% — server re-confirms. */
export function computeTotals(lines: CartLine[], taxRatePercent: number): CartTotals {
  const subtotal = round2(lines.reduce((s, l) => s + l.unitPrice * l.qty, 0));
  const taxableBase = lines.reduce((s, l) => (l.taxable ? s + l.unitPrice * l.qty : s), 0);
  const tax = round2((taxableBase * taxRatePercent) / 100);
  return { subtotal, tax, total: round2(subtotal + tax) };
}
